export function createPastelColourFromString(
  stringInput: string,
  opts: { saturation?: number; luminosity?: number } = {
    saturation: 95,
    luminosity: 75,
  }
) {
  // Create a pastel colour from the stringInput
  let stringUniqueHash = [...stringInput].reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  return `hsl(${stringUniqueHash % 360}, ${opts.saturation}%, ${
    opts.luminosity
  }%)`;
}

// We have a translations, CSV, 'name,#hex'. Splice it, take only the name values
export const getSwatchNameColourPairs = (swatchColours: string) =>
  swatchColours.split("\n").map((nameColourPair) => nameColourPair.trim());

// A list of only the values, in a [[1], [2], [3], ...] format.
// swatches expects string[][], where each internal array is a column within the swatch selector.
export const getSwatchColours = (swatchColours: string) =>
  getSwatchNameColourPairs(swatchColours).map((nameColourPair) => [
    nameColourPair.split(",")[1],
  ]);

// All hail the machines #ChatGPT
export function darkenHex(hex: string, percentage: number = 20): string {
  // Convert the hex value to RGB
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  // Darken each color by given percentage
  const darkR = Math.floor(r * (1 - percentage / 100));
  const darkG = Math.floor(g * (1 - percentage / 100));
  const darkB = Math.floor(b * (1 - percentage / 100));

  // Convert the darkened colors back to hex
  const darkHex =
    "#" +
    ((1 << 24) + (darkR << 16) + (darkG << 8) + darkB).toString(16).slice(1);

  return darkHex;
}

export function lightenHex(hex: string, percentage: number): string {
  // Convert the hex color to RGB
  const red = parseInt(hex.slice(1, 3), 16);
  const green = parseInt(hex.slice(3, 5), 16);
  const blue = parseInt(hex.slice(5, 7), 16);

  // Calculate the lightened RGB values
  const lightenFactor = 1 + percentage / 100;
  const lightenedRed = Math.min(255, Math.round(red * lightenFactor));
  const lightenedGreen = Math.min(255, Math.round(green * lightenFactor));
  const lightenedBlue = Math.min(255, Math.round(blue * lightenFactor));

  // Convert the lightened RGB values back to hex
  const lightenedHexColor =
    "#" +
    lightenedRed.toString(16).padStart(2, "0") +
    lightenedGreen.toString(16).padStart(2, "0") +
    lightenedBlue.toString(16).padStart(2, "0");

  return lightenedHexColor;
}

/**
 *  Check if a string is a valid 6 or 8 digit hex number
 */
function isValidHex(hex: string) {
  if (hex.indexOf("#") === 0) {
    hex = hex.slice(1);
  }

  return /^[0-9a-fA-F]{6,8}$/.test(hex);
}

/**
 *  Returns black/white, depending on the hex colour's luminance.
 *
 *  Returns undefined if an invalid hex string is given.
 */
export function convertHexToHighContrastColour(
  hex?: string | null
): string | undefined {
  if (!hex) {
    return undefined;
  }

  if (hex.indexOf("#") === 0) {
    hex = hex.slice(1);
  }

  if (hex.length < 3) {
    return undefined;
  }

  // convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  if (!isValidHex(hex)) {
    return undefined;
  }

  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luminance > 0.5 ? "#000000" : "#FFFFFF";
}
